/**
 * Country phone codes mapping
 * Maps country IDs from countries.ts to their international dialing codes
 */

import { ALL_COUNTRIES } from './countries';
import { getFlagEmoji } from '@/utils/countryFlags';

export type CountryCode = `+${string}`;

export type CountryCodeConfig = {
  flag: string;
  code: CountryCode;
  name: string;
};

/**
 * Map of country ID to phone dialing code
 * Based on ITU-T E.164 international numbering plan
 */
const COUNTRY_ID_TO_CODE: Record<string, CountryCode> = {
  'afghanistan': '+93',
  'albania': '+355',
  'algeria': '+213',
  'andorra': '+376',
  'angola': '+244',
  'antigua-and-barbuda': '+1268',
  'argentina': '+54',
  'armenia': '+374',
  'australia': '+61',
  'austria': '+43',
  'azerbaijan': '+994',
  'bahamas': '+1242',
  'bahrain': '+973',
  'bangladesh': '+880',
  'barbados': '+1246',
  'belarus': '+375',
  'belgium': '+32',
  'belize': '+501',
  'benin': '+229',
  'bhutan': '+975',
  'bolivia': '+591',
  'bosnia-and-herzegovina': '+387',
  'botswana': '+267',
  'brazil': '+55',
  'brunei': '+673',
  'bulgaria': '+359',
  'burkina-faso': '+226',
  'burundi': '+257',
  'cambodia': '+855',
  'cameroon': '+237',
  'canada': '+1',
  'cape-verde': '+238',
  'central-african-republic': '+236',
  'chad': '+235',
  'chile': '+56',
  'china': '+86',
  'colombia': '+57',
  'comoros': '+269',
  'congo': '+242',
  'costa-rica': '+506',
  'croatia': '+385',
  'cuba': '+53',
  'cyprus': '+357',
  'czech-republic': '+420',
  'democratic-republic-of-congo': '+243',
  'denmark': '+45',
  'djibouti': '+253',
  'dominica': '+1767',
  'dominican-republic': '+1809',
  'east-timor': '+670',
  'ecuador': '+593',
  'egypt': '+20',
  'el-salvador': '+503',
  'equatorial-guinea': '+240',
  'eritrea': '+291',
  'estonia': '+372',
  'eswatini': '+268',
  'ethiopia': '+251',
  'fiji': '+679',
  'finland': '+358',
  'france': '+33',
  'gabon': '+241',
  'gambia': '+220',
  'georgia': '+995',
  'germany': '+49',
  'ghana': '+233',
  'greece': '+30',
  'grenada': '+1473',
  'guatemala': '+502',
  'guinea': '+224',
  'guinea-bissau': '+245',
  'guyana': '+592',
  'haiti': '+509',
  'honduras': '+504',
  'hungary': '+36',
  'iceland': '+354',
  'india': '+91',
  'indonesia': '+62',
  'iran': '+98',
  'iraq': '+964',
  'ireland': '+353',
  'israel': '+972',
  'italy': '+39',
  'ivory-coast': '+225',
  'jamaica': '+1876',
  'japan': '+81',
  'jordan': '+962',
  'kazakhstan': '+7',
  'kenya': '+254',
  'kiribati': '+686',
  'kuwait': '+965',
  'kyrgyzstan': '+996',
  'laos': '+856',
  'latvia': '+371',
  'lebanon': '+961',
  'lesotho': '+266',
  'liberia': '+231',
  'libya': '+218',
  'liechtenstein': '+423',
  'lithuania': '+370',
  'luxembourg': '+352',
  'madagascar': '+261',
  'malawi': '+265',
  'malaysia': '+60',
  'maldives': '+960',
  'mali': '+223',
  'malta': '+356',
  'marshall-islands': '+692',
  'mauritania': '+222',
  'mauritius': '+230',
  'mexico': '+52',
  'micronesia': '+691',
  'moldova': '+373',
  'monaco': '+377',
  'mongolia': '+976',
  'montenegro': '+382',
  'morocco': '+212',
  'mozambique': '+258',
  'myanmar': '+95',
  'namibia': '+264',
  'nauru': '+674',
  'nepal': '+977',
  'netherlands': '+31',
  'new-zealand': '+64',
  'nicaragua': '+505',
  'niger': '+227',
  'nigeria': '+234',
  'north-korea': '+850',
  'north-macedonia': '+389',
  'norway': '+47',
  'oman': '+968',
  'pakistan': '+92',
  'palau': '+680',
  'panama': '+507',
  'papua-new-guinea': '+675',
  'paraguay': '+595',
  'peru': '+51',
  'philippines': '+63',
  'poland': '+48',
  'portugal': '+351',
  'qatar': '+974',
  'romania': '+40',
  'russia': '+7',
  'rwanda': '+250',
  'saint-kitts-and-nevis': '+1869',
  'saint-lucia': '+1758',
  'saint-vincent-and-grenadines': '+1784',
  'samoa': '+685',
  'san-marino': '+378',
  'sao-tome-and-principe': '+239',
  'saudi-arabia': '+966',
  'senegal': '+221',
  'serbia': '+381',
  'seychelles': '+248',
  'sierra-leone': '+232',
  'singapore': '+65',
  'slovakia': '+421',
  'slovenia': '+386',
  'solomon-islands': '+677',
  'somalia': '+252',
  'south-africa': '+27',
  'south-korea': '+82',
  'south-sudan': '+211',
  'spain': '+34',
  'sri-lanka': '+94',
  'sudan': '+249',
  'suriname': '+597',
  'sweden': '+46',
  'switzerland': '+41',
  'syria': '+963',
  'taiwan': '+886',
  'tajikistan': '+992',
  'tanzania': '+255',
  'thailand': '+66',
  'togo': '+228',
  'tonga': '+676',
  'trinidad-and-tobago': '+1868',
  'tunisia': '+216',
  'turkey': '+90',
  'turkmenistan': '+993',
  'tuvalu': '+688',
  'uganda': '+256',
  'ukraine': '+380',
  'united-arab-emirates': '+971',
  'united-kingdom': '+44',
  'united-states': '+1',
  'uruguay': '+598',
  'uzbekistan': '+998',
  'vanuatu': '+678',
  'vatican-city': '+39',
  'venezuela': '+58',
  'vietnam': '+84',
  'yemen': '+967',
  'zambia': '+260',
  'zimbabwe': '+263',
};

/**
 * Generate country codes list from ALL_COUNTRIES with flags and codes
 */
export function getCountryCodes(): CountryCodeConfig[] {
  return ALL_COUNTRIES.map((country) => {
    const code = COUNTRY_ID_TO_CODE[country.id] || '+1'; // Default to +1 if not found
    const flag = getFlagEmoji(country.label) || getFlagEmoji(country.id) || '🌍';
    
    return {
      flag,
      code,
      name: country.label,
    };
  }).sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name
}

/**
 * Get country code config by code
 */
export function getCountryCodeByCode(code: CountryCode): CountryCodeConfig | undefined {
  return getCountryCodes().find((item) => item.code === code);
}

/**
 * Get country code config by country ID
 */
export function getCountryCodeById(countryId: string): CountryCodeConfig | undefined {
  const code = COUNTRY_ID_TO_CODE[countryId];
  if (!code) return undefined;
  
  const country = ALL_COUNTRIES.find((c) => c.id === countryId);
  if (!country) return undefined;
  
  const flag = getFlagEmoji(country.label) || getFlagEmoji(country.id) || '🌍';
  
  return {
    flag,
    code,
    name: country.label,
  };
}

/**
 * Pre-computed country codes list for better performance
 */
export const COUNTRY_CODES: CountryCodeConfig[] = getCountryCodes();

